if (!customElements.get('video-sound-toggle')) {
  class VideoSoundToggle extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('.slideshow-item') || this.parentElement;
      this.muteButton = this.querySelector('[data-video-mute]');
      this.playButton = this.querySelector('[data-video-play]');
      this.hint = this.querySelector('.slideshow-video-controls__hint');
      this.hintShown = false;
      this.inView = false;

      if (this.muteButton) this.muteButton.addEventListener('click', this.onMuteClick.bind(this));
      if (this.playButton) this.playButton.addEventListener('click', this.onPlayClick.bind(this));

      this.waitForVideo();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
      clearTimeout(this.hintTimer);
    }

    // viewport-media injects the <video> lazily, so wait for it to appear
    waitForVideo() {
      const video = this.container.querySelector('video');
      if (video) return this.bindVideo(video);

      this.observer = new MutationObserver(() => {
        const lazyVideo = this.container.querySelector('video');
        if (lazyVideo) {
          this.observer.disconnect();
          this.bindVideo(lazyVideo);
        }
      });
      this.observer.observe(this.container, { childList: true, subtree: true });
    }

    bindVideo(video) {
      this.video = video;
      ['play', 'pause', 'volumechange'].forEach((event) => video.addEventListener(event, () => this.sync()));
      video.addEventListener('playing', () => this.maybeShowHint());

      this.classList.add('is-ready');
      this.sync();
      this.observeView();
    }

    // Only show the hint once the button is actually on screen, not while the video preloads off-screen
    observeView() {
      if (!this.hint) return;
      if (!('IntersectionObserver' in window)) {
        this.inView = true;
        return this.maybeShowHint();
      }
      this.viewObserver = new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
          if (this.inView) this.maybeShowHint();
        },
        { threshold: 1 }
      );
      this.viewObserver.observe(this);
    }

    onMuteClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.video) return;

      this.video.muted = !this.video.muted;
      if (!this.video.muted) {
        if (this.video.volume === 0) this.video.volume = 1;
        if (this.video.paused) this.video.play().catch(() => {});
        this.pauseSlideshow();
      }
      this.hideHint();
    }

    onPlayClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.video) return;

      if (this.video.paused) {
        this.video.play().catch(() => {});
        this.pauseSlideshow();
      } else {
        this.video.pause();
      }
    }

    // Stop the slideshow rotating away while someone is watching or listening
    pauseSlideshow() {
      const slideshow = this.closest('slide-show');
      const autoplay = slideshow && slideshow.slideInstance && slideshow.slideInstance.Components.Autoplay;
      if (autoplay) autoplay.pause();
    }

    sync() {
      if (!this.video) return;
      const muted = this.video.muted || this.video.volume === 0;
      const paused = this.video.paused;

      this.classList.toggle('is-muted', muted);
      this.classList.toggle('is-paused', paused);

      if (this.muteButton) {
        this.muteButton.setAttribute('aria-pressed', String(!muted));
        this.muteButton.setAttribute(
          'aria-label',
          muted ? this.muteButton.dataset.labelUnmute : this.muteButton.dataset.labelMute
        );
      }
      if (this.playButton) {
        this.playButton.setAttribute(
          'aria-label',
          paused ? this.playButton.dataset.labelPlay : this.playButton.dataset.labelPause
        );
      }
      if (!muted) this.hideHint();
    }

    maybeShowHint() {
      if (!this.hint || this.hintShown || !this.inView || !this.video.muted || this.video.paused) return;
      this.hintShown = true;
      if (this.viewObserver) this.viewObserver.disconnect();
      this.classList.add('show-hint');
      this.hintTimer = setTimeout(() => this.hideHint(), 6000);
    }

    hideHint() {
      clearTimeout(this.hintTimer);
      this.classList.remove('show-hint');
    }
  }

  customElements.define('video-sound-toggle', VideoSoundToggle);
}
